import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import Select from 'components/Select';
import { selectStore, storeLoadingState } from 'redux/slices/javelinStore';
import {
  selectCompany,
  getCompanyList,
  companyAndStoreSelector,
} from 'redux/slices/company';
import { CustomOption } from 'utils/customComponents';
import { useDebounce } from 'hooks';

import './style.css';

const StoreSelectDropdown = ({ selectCallback }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 300);
  const companyAndStoreList = useSelector(companyAndStoreSelector);
  const loadingStores = useSelector(storeLoadingState);

  const {
    storeList,
    companyList,
    searchResult,
    currentStore,
    currentCompany,
    isSearching,
  } = companyAndStoreList;

  const promiseRef = useRef(null);

  const dispatch = useDispatch();

  const onSelectStore = (item) => {
    if (currentStore.id !== item.id) {
      dispatch(selectStore(item.id)).then(() => selectCallback());
    }
  };

  const onSelectCompany = (item) => {
    if (currentCompany.id !== item.id) {
      dispatch(selectCompany({ companyId: item.id })).then(() =>
        selectCallback()
      );
    }
  };

  const companyOptions = useMemo(() => {
    return debouncedSearchTerm ? searchResult : companyList;
  }, [companyList, debouncedSearchTerm, searchResult]);

  useEffect(() => {
    if (!debouncedSearchTerm) return;
    if (debouncedSearchTerm.length) {
      if (promiseRef.current) {
        promiseRef.current.abort();
      }
      promiseRef.current = dispatch(
        getCompanyList({ term: debouncedSearchTerm })
      );
    }
  }, [dispatch, debouncedSearchTerm]);

  return (
    <div className="px-5 mb-1 min-h-20">
      {!!companyList.length && (
        <>
          <Select
            className="store-select"
            options={companyOptions}
            value={currentCompany.id ? currentCompany : undefined}
            getOptionValue={(option) => option.id}
            getOptionLabel={(option) => option.name}
            onChange={onSelectCompany}
            placeholder="Select a company"
            onInputChange={(s) => setSearchTerm(s)}
            testId="company-select"
            inputId="input"
            noOptionsMessage={() =>
              isSearching ? 'Searching...' : 'No options'
            }
            components={{
              IndicatorSeparator: () => null,
              Option: CustomOption,
            }}
          />
        </>
      )}
      {!!Object.keys(currentCompany).length && !loadingStores && !!storeList.length && (
        <div id="store-select" className="flex items-center h-10 -mt-1">
          {storeList.length > 1 ? (
            <>
              <span className="mr-2 select-none text-javelin-gray-light">
                &#10551;
              </span>
              <Select
                className="store-select"
                testId="store-select"
                options={storeList}
                value={currentStore.id ? currentStore : undefined}
                getOptionValue={(option) => option.id}
                getOptionLabel={(option) => option.name}
                isSearchable={false}
                onChange={onSelectStore}
                placeholder="Select a store"
                components={{
                  IndicatorSeparator: () => null,
                  Option: CustomOption,
                }}
              />
            </>
          ) : (
            <p className="ml-6 -mt-1 text-sm cursor-default">
              {currentStore.name}
            </p>
          )}
        </div>
      )}
    </div>
  );
};
export default StoreSelectDropdown;
